import {
  getCampaignTimezone,
  getCampaignServerTime,
  getCampaignCurrentTime,
  setCampaignCurrentTime
} from '@/redux/campaign';

import Store from '@/redux';
import { isProduction } from '@/utils/Location';

class Time {
  // Get server date in local timezone:
  public getServerDate(): Date | undefined {
    const time = getCampaignServerTime(Store.getState());
    return (time && new Date(time)) || undefined;
  }

  // Get server time in UTC+0 timezone:
  public getServerISOTime(): string | undefined {
    return this.getServerDate()?.toISOString();
  }

  // Get server date in campaign timezone:
  public getCampaignServerDate(): Date | undefined {
    const time = this.getServerDate();
    if (!time) return undefined;

    time.setTime(
      new Date(time.toISOString().slice(0, -1)).getTime() +
        this.getCampaignTimezoneOffset(time)
    );

    return time;
  }

  // Get current time in local timezone:
  public getCurrentDate(): Date | void {
    const time = this.getCurrentISOTime();
    if (!time) return undefined;
    return new Date(time);
  }

  // Get current (or local) time in UTC+0 timezone:
  public getCurrentISOTime(always = false): string | null | undefined {
    const time = getCampaignCurrentTime(Store.getState());
    return time ?? ((always && new Date().toISOString()) || time);
  }

  // Get current (or local) date in campaign timezone:
  public getCampaignCurrentDate(always = false): Date | undefined {
    const time = this.getCurrentISOTime(always);
    if (!time) return undefined;

    const date = new Date(time);

    date.setTime(
      new Date(time.slice(0, -1)).getTime() +
        this.getCampaignTimezoneOffset(date)
    );

    return date;
  }

  // Get difference (in minutes) between UTC+0 and campaign timezone:
  public getCampaignTimezoneOffset(time: Date): number {
    const timeZone = getCampaignTimezone(Store.getState());

    return (
      new Date(time.toLocaleString('en-US', { timeZone })).getTime() -
      new Date(time.toISOString().slice(0, -1)).getTime()
    );
  }

  // Get difference (in minutes) between local and campaign timezone:
  public getLocalTimezoneOffset(): number {
    return new Date().getTimezoneOffset() * -6e4;
  }

  // Set current time from URL params:
  public setCurrentTime(params: string): void {
    const search = new URLSearchParams(params);
    const time = search.get('currentTime');

    if (isProduction || !time) return;
    Store.dispatch(setCampaignCurrentTime(time));
  }

  // Get current time for API as query param:
  public getCurrentTimeParam(first = true): string {
    const time = getCampaignCurrentTime(Store.getState());
    return (time && `${(first && '?') || '&'}currentTime=${time}`) || '';
  }

  // Get current time for API as body data:
  public getCurrentTimeData(): { currentTime: string } | false {
    const time = getCampaignCurrentTime(Store.getState());
    return !!time && { currentTime: time };
  }
}

export default new Time();
